/**
 * TAAR — taar-global.js
 * Global runtime: EventBus · SectionRegistry · Cart API · Utilities · Animations
 * Loaded in <head> via defer. All other section JS depends on window.TAAR being ready.
 */

(function (global) {
  'use strict';

  /* ─────────────────────────────────────────────────────────────────────────
   * EventBus — lightweight pub/sub for cross-component communication.
   *
   * Published events (convention):
   *   cart:item-added   { item }
   *   cart:updated      { cart }
   *   cart:opened
   *   cart:closed
   *   search:opened
   *   search:closed
   *   wishlist:added    { id, handle }
   *   wishlist:removed  { id }
   *   variant:changed   { productId, variant }
   *   nav:opened
   *   nav:closed
   * ─────────────────────────────────────────────────────────────────────── */

  var EventBus = (function () {
    var listeners = {};

    return {
      on: function (event, fn) {
        if (!listeners[event]) listeners[event] = [];
        listeners[event].push(fn);
        return function off() { EventBus.off(event, fn); };
      },

      off: function (event, fn) {
        if (!listeners[event]) return;
        listeners[event] = listeners[event].filter(function (l) { return l !== fn; });
      },

      emit: function (event, data) {
        if (!listeners[event]) return;
        listeners[event].slice().forEach(function (fn) {
          try { fn(data); } catch (e) { console.error('[TAAR EventBus]', event, e); }
        });
      },

      once: function (event, fn) {
        var unsub = EventBus.on(event, function (data) {
          unsub();
          fn(data);
        });
        return unsub;
      }
    };
  }());

  /* ─────────────────────────────────────────────────────────────────────────
   * SectionRegistry — maps section types to init/destroy callbacks.
   * Sections call TAAR.SectionRegistry.register() in their {% javascript %}.
   * The harness calls init on DOMContentLoaded and on shopify:section:load.
   * ─────────────────────────────────────────────────────────────────────── */

  var SectionRegistry = (function () {
    var registry = {};

    return {
      register: function (type, init, destroy) {
        registry[type] = {
          init: init || null,
          destroy: destroy || null
        };
      },

      init: function (container) {
        var el = container.matches('[data-section-type]')
          ? container
          : container.querySelector('[data-section-type]');
        if (!el) return;

        var type = el.dataset.sectionType;
        var entry = registry[type];
        if (entry && entry.init) {
          try { entry.init(el); }
          catch (e) { console.error('[TAAR SectionRegistry] init error:', type, e); }
        }
      },

      destroy: function (container) {
        var el = container.matches('[data-section-type]')
          ? container
          : container.querySelector('[data-section-type]');
        if (!el) return;

        var type = el.dataset.sectionType;
        var entry = registry[type];
        if (entry && entry.destroy) {
          try { entry.destroy(el); }
          catch (e) { console.error('[TAAR SectionRegistry] destroy error:', type, e); }
        }
      },

      initAll: function (scope) {
        var els = (scope || document).querySelectorAll('[data-section-type]');
        els.forEach(function (el) {
          var type = el.dataset.sectionType;
          var entry = registry[type];
          if (entry && entry.init) {
            try { entry.init(el); }
            catch (e) { console.error('[TAAR SectionRegistry] init error:', type, e); }
          }
        });
      }
    };
  }());

  /* ─────────────────────────────────────────────────────────────────────────
   * Cart — Shopify Cart API wrappers (AJAX).
   * All methods return Promises. On success they emit EventBus events.
   * ─────────────────────────────────────────────────────────────────────── */

  var Cart = {
    _headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },

    getState: function () {
      return fetch('/cart.js', { headers: Cart._headers })
        .then(function (r) { return r.json(); });
    },

    addItem: function (variantId, quantity, properties) {
      /* Single-piece store: every saree is a one-of-a-kind piece, so a variant
         may exist in the cart at most once. Never add more than 1, and if it is
         already in the bag treat the add as a no-op success (open the drawer,
         don't bump the quantity to 2). */
      return Cart.getState().then(function (cart) {
        var already = (cart.items || []).some(function (line) {
          return String(line.id) === String(variantId) ||
                 String(line.variant_id) === String(variantId);
        });

        if (already) {
          EventBus.emit('cart:item-added', { item: null, already: true });
          return cart;
        }

        var body = { id: variantId, quantity: 1 };
        if (properties) body.properties = properties;

        return fetch('/cart/add.js', {
          method: 'POST',
          headers: Cart._headers,
          body: JSON.stringify(body)
        }).then(function (r) {
          if (!r.ok) return r.json().then(function (err) { throw err; });
          return r.json();
        }).then(function (item) {
          EventBus.emit('cart:item-added', { item: item });
          return item;
        });
      });
    },

    updateItem: function (key, quantity) {
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: Cart._headers,
        body: JSON.stringify({ id: key, quantity: quantity })
      }).then(function (r) { return r.json(); })
        .then(function (cart) {
          EventBus.emit('cart:updated', { cart: cart });
          return cart;
        });
    },

    updateItems: function (updates) {
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: Cart._headers,
        body: JSON.stringify({ updates: updates })
      }).then(function (r) { return r.json(); })
        .then(function (cart) {
          EventBus.emit('cart:updated', { cart: cart });
          return cart;
        });
    },

    /* Fetch re-rendered section HTML for cart drawer UI */
    getSectionHTML: function (sectionIds) {
      var ids = Array.isArray(sectionIds) ? sectionIds.join(',') : sectionIds;
      return fetch('/cart?sections=' + ids, { headers: Cart._headers })
        .then(function (r) { return r.json(); });
    }
  };

  /* ─────────────────────────────────────────────────────────────────────────
   * Wishlist — localStorage-backed wishlist. No account required.
   * Schema: { items: [{ id, variantId, handle, title, price, image, url, addedAt }] }
   * ─────────────────────────────────────────────────────────────────────── */

  var Wishlist = (function () {
    var KEY = 'taar_wishlist';

    function read() {
      try {
        var raw = localStorage.getItem(KEY);
        var data = raw ? JSON.parse(raw) : { items: [] };
        if (!data || !Array.isArray(data.items)) return { items: [] };

        /* Keep the wishlist resilient to stale or malformed browser data. */
        return {
          items: data.items.filter(function (item) {
            return item && item.id !== undefined && item.id !== null && item.id !== '';
          })
        };
      } catch (_) {
        return { items: [] };
      }
    }

    function write(data) {
      try { localStorage.setItem(KEY, JSON.stringify(data)); } catch (_) {}
    }

    function isSameProduct(item, productId) {
      return String(item.id) === String(productId);
    }

    /* Product URLs from collection cards can carry collection context. A
       wishlist should always use Shopify's canonical product route instead. */
    function canonicalProductUrl(handle, fallbackUrl) {
      var cleanHandle = typeof handle === 'string' ? handle.trim() : '';
      if (cleanHandle) {
        return '/products/' + encodeURIComponent(cleanHandle);
      }

      var cleanUrl = typeof fallbackUrl === 'string' ? fallbackUrl.trim() : '';
      if (/^\/products\/[^/?#]+(?:[?#].*)?$/.test(cleanUrl)) return cleanUrl;
      return '';
    }

    function normaliseItem(item) {
      return {
        id: item.id,
        variantId: item.variantId || null,
        handle: item.handle || '',
        title: item.title || '',
        price: Number(item.price) || 0,
        image: item.image || '',
        url: canonicalProductUrl(item.handle, item.url),
        requiresOptions: item.requiresOptions === true,
        addedAt: item.addedAt || ''
      };
    }

    return {
      getAll: function () {
        return read().items.map(normaliseItem);
      },

      has: function (productId) {
        return read().items.some(function (i) { return isSameProduct(i, productId); });
      },

      add: function (item) {
        if (!item || item.id === undefined || item.id === null || item.id === '') return;
        var data = read();
        if (data.items.some(function (i) { return isSameProduct(i, item.id); })) return;

        var savedItem = normaliseItem(item);
        savedItem.addedAt = new Date().toISOString();

        data.items.unshift(savedItem);
        write(data);
        EventBus.emit('wishlist:added', { id: savedItem.id, handle: savedItem.handle });
      },

      remove: function (productId) {
        var data = read();
        data.items = data.items.filter(function (i) { return !isSameProduct(i, productId); });
        write(data);
        EventBus.emit('wishlist:removed', { id: productId });
      },

      toggle: function (item) {
        if (Wishlist.has(item.id)) {
          Wishlist.remove(item.id);
        } else {
          Wishlist.add(item);
        }
      },

      count: function () { return read().items.length; }
    };
  }());

  /* ─────────────────────────────────────────────────────────────────────────
   * Util — general-purpose helpers.
   * ─────────────────────────────────────────────────────────────────────── */

  var Util = {
    debounce: function (fn, delay) {
      var timer;
      return function () {
        var ctx = this, args = arguments;
        clearTimeout(timer);
        timer = setTimeout(function () { fn.apply(ctx, args); }, delay);
      };
    },

    throttle: function (fn, limit) {
      var inThrottle;
      return function () {
        var ctx = this, args = arguments;
        if (!inThrottle) {
          fn.apply(ctx, args);
          inThrottle = true;
          setTimeout(function () { inThrottle = false; }, limit);
        }
      };
    },

    /* Trap keyboard focus inside a container (for modals/drawers). */
    trapFocus: function (element, firstFocusable) {
      var focusableSelectors = [
        'a[href]', 'button:not([disabled])', 'textarea',
        'input:not([type="hidden"])', 'select',
        '[tabindex]:not([tabindex="-1"])'
      ].join(',');

      var focusable = Array.from(element.querySelectorAll(focusableSelectors))
        .filter(function (el) { return !el.closest('[hidden]'); });

      var first = firstFocusable || focusable[0];
      var last  = focusable[focusable.length - 1];

      if (first) first.focus();

      function handleKeyDown(e) {
        if (e.key !== 'Tab') return;
        if (e.shiftKey) {
          if (document.activeElement === first) { e.preventDefault(); last.focus(); }
        } else {
          if (document.activeElement === last)  { e.preventDefault(); first.focus(); }
        }
      }

      element.addEventListener('keydown', handleKeyDown);
      return function releaseFocus() {
        element.removeEventListener('keydown', handleKeyDown);
      };
    },

    /* Lock/unlock body scroll (for open drawers/modals). */
    lockScroll: function () {
      var scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.style.setProperty('--scrollbar-width', scrollbarWidth + 'px');
      document.body.classList.add('scroll-lock');
    },

    unlockScroll: function () {
      document.body.classList.remove('scroll-lock');
    },

    /* Format money using Shopify's money_format. Falls back to {{amount}} if unset. */
    formatMoney: function (cents, format) {
      format = format || (global.Shopify && global.Shopify.money_format) || '{{amount}}';
      var amount = (Math.round(cents) / 100).toFixed(2);
      var noDecimal = Math.floor(cents / 100).toString();
      return format
        .replace(/\{\{\s*amount\s*\}\}/, amount)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/, noDecimal);
    }
  };

  /* ─────────────────────────────────────────────────────────────────────────
   * Animations — IntersectionObserver-based scroll reveal.
   * Watches [data-animate] elements; adds 'is-visible' when they enter view.
   * The CSS transition in base.css does the actual visual work.
   * Respects prefers-reduced-motion by not observing if user prefers no motion.
   * ─────────────────────────────────────────────────────────────────────── */

  var Animations = (function () {
    var observer = null;
    var reducedMotion = global.matchMedia &&
      global.matchMedia('(prefers-reduced-motion: reduce)').matches;

    /* Also check settings.section_animation injected by Liquid */
    var animationsDisabled = reducedMotion ||
      (global.TAAR_SETTINGS && global.TAAR_SETTINGS.section_animation === 'disabled');

    function observe(el) {
      if (animationsDisabled) {
        el.classList.add('is-visible');
        return;
      }
      if (observer) observer.observe(el);
    }

    return {
      init: function () {
        if (animationsDisabled || !global.IntersectionObserver) {
          document.querySelectorAll('[data-animate]').forEach(function (el) {
            el.classList.add('is-visible');
          });
          return;
        }

        observer = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          });
        }, { threshold: 0.15, rootMargin: '0px 0px -40px 0px' });

        document.querySelectorAll('[data-animate]').forEach(observe);
      },

      /* Call after dynamic content is injected (e.g. after section reload). */
      refresh: function (scope) {
        (scope || document).querySelectorAll('[data-animate]:not(.is-visible)').forEach(observe);
      }
    };
  }());

  /* ─────────────────────────────────────────────────────────────────────────
   * PredictiveSearch — lightweight wrapper for Shopify's suggest API.
   * ─────────────────────────────────────────────────────────────────────── */

  var PredictiveSearch = {
    _cache: {},
    _abortController: null,
    SECTION_ID: 'predictive-search',

    /* Build a fresh AbortController, cancelling any in-flight request so only
       the latest keystroke resolves. */
    _newSignal: function () {
      if (PredictiveSearch._abortController) {
        PredictiveSearch._abortController.abort();
      }
      if (global.AbortController) {
        PredictiveSearch._abortController = new global.AbortController();
        return PredictiveSearch._abortController.signal;
      }
      return undefined;
    },

    /* Render predictive results as ready-to-inject HTML via the Section
       Rendering API. This is the recommended Shopify approach: results are
       rendered in Liquid (correct images, money, sale prices, tracked URLs)
       instead of hand-templating raw suggest.json in JavaScript.
       Returns a Promise<string> of the section's HTML. */
    render: function (term, types, limit) {
      types = types || 'product,collection,article';
      limit = limit || 6;
      var cacheKey = 'html|' + term + '|' + types + '|' + limit;

      if (PredictiveSearch._cache[cacheKey]) {
        return Promise.resolve(PredictiveSearch._cache[cacheKey]);
      }

      var signal = PredictiveSearch._newSignal();

      var url = '/search/suggest?q=' + encodeURIComponent(term) +
        '&section_id=' + PredictiveSearch.SECTION_ID +
        '&resources[type]=' + encodeURIComponent(types) +
        '&resources[limit]=' + limit +
        '&resources[options][unavailable_products]=last' +
        '&resources[options][fields]=title,product_type,variants.title,vendor';

      var opts = {};
      if (signal) opts.signal = signal;

      return fetch(url, opts)
        .then(function (r) { return r.text(); })
        .then(function (html) {
          PredictiveSearch._cache[cacheKey] = html;
          return html;
        })
        .catch(function (err) {
          if (err.name !== 'AbortError') throw err;
          return null;
        });
    },

    /* Raw JSON access to Shopify's suggest API (kept for programmatic use). */
    query: function (term, types) {
      types = types || 'product,collection,article';
      var cacheKey = 'json|' + term + '|' + types;

      if (PredictiveSearch._cache[cacheKey]) {
        return Promise.resolve(PredictiveSearch._cache[cacheKey]);
      }

      var signal = PredictiveSearch._newSignal();

      var url = '/search/suggest.json?q=' + encodeURIComponent(term) +
        '&resources[type]=' + encodeURIComponent(types) +
        '&resources[limit]=6' +
        '&resources[options][unavailable_products]=last';

      var opts = {};
      if (signal) opts.signal = signal;

      return fetch(url, opts)
        .then(function (r) { return r.json(); })
        .then(function (data) {
          PredictiveSearch._cache[cacheKey] = data;
          return data;
        })
        .catch(function (err) {
          if (err.name !== 'AbortError') throw err;
        });
    }
  };

  /* ─────────────────────────────────────────────────────────────────────────
   * Toast — lightweight status messages replacing browser alert().
   * Renders a single floating region at the bottom of the viewport.
   * Toasts slide up and fade in; auto-dismiss after `duration` ms.
   * ─────────────────────────────────────────────────────────────────────── */

  var Toast = (function () {
    var region = null;
    var stylesInjected = false;

    function injectStyles() {
      if (stylesInjected) return;
      stylesInjected = true;
      var s = document.createElement('style');
      s.textContent = [
        '#taar-toast-region{',
          'position:fixed;bottom:28px;left:50%;transform:translateX(-50%);',
          'z-index:99998;display:flex;flex-direction:column;align-items:center;',
          'gap:8px;pointer-events:none;',
        '}',
        '.taar-toast{',
          'font-family:var(--font-body-family,sans-serif);',
          'font-size:11px;font-weight:500;letter-spacing:0.1em;text-transform:uppercase;',
          'color:var(--color-cream,#faf7f2);',
          'background:rgba(26,24,20,0.9);',
          'padding:10px 22px;',
          'backdrop-filter:blur(8px);-webkit-backdrop-filter:blur(8px);',
          'opacity:0;transform:translateY(10px);',
          'transition:opacity 200ms ease,transform 200ms ease;',
          'white-space:nowrap;',
        '}',
        '.taar-toast.is-visible{opacity:1;transform:translateY(0);}',
        '.taar-toast.is-leaving{opacity:0;transform:translateY(-4px);}'
      ].join('');
      document.head.appendChild(s);
    }

    function getRegion() {
      if (region) return region;
      injectStyles();
      region = document.createElement('div');
      region.id = 'taar-toast-region';
      region.setAttribute('aria-live', 'polite');
      region.setAttribute('aria-atomic', 'true');
      document.body.appendChild(region);
      return region;
    }

    return {
      show: function (message, duration) {
        var r = getRegion();
        duration = duration || 2400;

        var el = document.createElement('div');
        el.className = 'taar-toast';
        el.textContent = message;
        r.appendChild(el);

        el.offsetHeight; /* force reflow */
        el.classList.add('is-visible');

        var timer = setTimeout(function () {
          el.classList.add('is-leaving');
          el.classList.remove('is-visible');
          setTimeout(function () {
            if (el.parentNode) el.parentNode.removeChild(el);
          }, 260);
        }, duration);

        return function cancel() {
          clearTimeout(timer);
          if (el.parentNode) el.parentNode.removeChild(el);
        };
      }
    };
  }());

  /* ─────────────────────────────────────────────────────────────────────────
   * Bootstrap — wire everything together on DOMContentLoaded.
   * ─────────────────────────────────────────────────────────────────────── */

  document.addEventListener('DOMContentLoaded', function () {
    SectionRegistry.initAll(document);
    Animations.init();
  });

  /* Shopify Theme Editor API hooks */
  document.addEventListener('shopify:section:load', function (e) {
    SectionRegistry.init(e.target);
    Animations.refresh(e.target);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    SectionRegistry.destroy(e.target);
  });

  document.addEventListener('shopify:section:reorder', function () {
    Animations.refresh(document);
  });

  /* ─────────────────────────────────────────────────────────────────────────
   * Public API
   * ─────────────────────────────────────────────────────────────────────── */

  global.TAAR = {
    EventBus:         EventBus,
    SectionRegistry:  SectionRegistry,
    Cart:             Cart,
    Wishlist:         Wishlist,
    Util:             Util,
    Animations:       Animations,
    PredictiveSearch: PredictiveSearch,
    Toast:            Toast
  };

}(window));
